// ============================================================
// MapFolderView - a folder of maps, browsed and imported in bulk.
//
// The character folder view, for maps. Same directory input, same
// shelf, same click / right-click / shift-range grammar, so there is
// one thing to learn rather than two.
//
// WHY A DIRECTORY INPUT AND NOT `showDirectoryPicker`: the session only
// ever READS here. The picker API buys write access nobody wants and
// charges a secure context for it - over a dev server reached across a
// tailnet it does not exist at all, and it is Chromium-only besides.
// `<input type="file" webkitdirectory>` works everywhere and on plain
// HTTP. That is the whole argument of `utils/folderFiles.ts` (v8.23.1),
// and it applies here unchanged.
//
// The maps folder has the character folder's shape exactly, so this
// shares `treeFromFileList` and `asShelfTree` rather than growing a
// second reader for the same layout.
// ============================================================
import { useMemo, useRef, useState } from 'react';
import CharacterShelf from './workshop/CharacterShelf';
import { treeFromFileList, allFileCharacters, fileCharactersAt, asShelfTree } from '../utils/folderFiles';
import { readMapsFromFiles } from '../utils/mapFolderImport';
import { applySelection, toggleGroupSelection } from '../utils/listSelection';
import { DIRECTORY_INPUT } from '../utils/directoryInput';
// The SAME stylesheet the character folder view uses - the two
// browsers are one thing to learn, so they should not look like two.
import styles from '../styles/Workshop.module.css';
import common from '../styles/common.module.css';

/** `Group/Name`, or `Name` at the root - the shelf's path grammar. */
const pathOf = (m) => (m.group ? `${m.group}/${m.name}` : m.name);

/** A pin wins over the latest - the only way one map breaks ranks. */
function resolvePins(entries, pinned) {
  return entries.map((m) => {
    const pin = pinned[pathOf(m)];
    return pin ? { ...m, file: m.iterations.find((f) => f.name === pin) ?? m.file } : m;
  });
}

/** What an import says about itself: how many from where, and which files could not be read. */
function importReport(out, what) {
  const parts = [`Imported ${out.maps.length} from ${what}`];
  if (out.failed.length) parts.push(`could not read ${out.failed.join(', ')}`);
  return parts.join(' · ');
}

/**
 * Read some maps and hand them over.
 *
 * The report is the point: a folder of twenty with one bad file imports
 * the nineteen and NAMES the twentieth, rather than failing silently the
 * way the single-file import does.
 */
function useFolderImport(onImport, pinned) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const importThese = async (entries, what) => {
    if (!entries.length) return;
    setBusy(true);
    setNote(null);
    try {
      const out = await readMapsFromFiles(resolvePins(entries, pinned));
      if (out.maps.length) onImport(out.maps);
      setNote(importReport(out, what));
    } finally {
      setBusy(false);
    }
  };
  return { busy, note, setNote, importThese };
}

/**
 * Which maps are picked, and which groups are folded. The draw order and
 * the shift-range anchor are REFS, not state: the shelf reports the order
 * from an effect, and setting state there renders again, which fires it
 * again (see CharacterShelf's `onOrder`).
 */
function useShelfPicks() {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const order = useRef([]);
  const anchor = useRef(null);
  const toggleGroup = (name) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  // applySelection takes a SelectionInput and returns { selected, anchor } -
  // feeding the RESULT into state was the v8.199.16 crash: the object has
  // no .has, and the next render died inside fileCharactersAt.
  const toggleSelect = (path, e, remove) =>
    setSelected((prev) => {
      const out = applySelection({ order: order.current, selected: prev, anchor: anchor.current }, path, {
        shift: e.shiftKey,
        remove,
      });
      anchor.current = out.anchor;
      return out.selected;
    });
  return { collapsed, toggleGroup, selected, setSelected, toggleSelect, order };
}

/**
 * The directory input. `webkitdirectory` is not in the JSX type, and the
 * two spellings are what browsers actually read - both are set; either
 * alone leaves somebody out.
 */
function FolderInput({ inputRef, onPick }) {
  return (
    <input
      ref={inputRef}
      type="file"
      {...DIRECTORY_INPUT}
      multiple
      style={{ display: 'none' }}
      data-testid="map-folder-input"
      onChange={(e) => onPick(e.target.files)}
    />
  );
}

/** Before a folder is chosen: the picker, and what a maps folder looks like. */
function ClosedFolder({ input, onChoose }) {
  return (
    <div className={styles.folderStart} data-testid="map-folder-closed">
      {input}
      <p className={styles.hint}>
        A folder of maps, laid out the way the Workshop writes them - a folder per map, a file per version. Groups come
        in as groups.
      </p>
      <button className={common.btnSmall} data-testid="map-folder-open" onClick={onChoose}>
        Choose folder…
      </button>
    </div>
  );
}

function FolderActions({ all, chosen, what, busy, picks, importThese, onChange }) {
  return (
    <div className={styles.charHeaderActions}>
      <button
        className={common.btnSmall}
        data-testid="map-folder-import-all"
        disabled={busy || all.length === 0}
        onClick={() => importThese(all, what)}
      >
        ⤓ Import all ({all.length})
      </button>
      <button
        className={common.btnSmall}
        data-testid="map-folder-import-selected"
        disabled={busy || chosen.length === 0}
        onClick={() => importThese(chosen, `${chosen.length} selected`)}
      >
        Import selected ({chosen.length})
      </button>
      <button
        className={common.btnSmall}
        data-testid="map-folder-clear"
        disabled={picks.selected.size === 0}
        onClick={() => picks.setSelected(new Set())}
      >
        Clear
      </button>
      <button className={common.btnSmall} data-testid="map-folder-change" onClick={onChange}>
        Change folder
      </button>
    </div>
  );
}

/**
 * Which version of a map to import. ONE ROW, ONE VERSION: a map with a
 * single version has nothing to choose, and a select there is furniture.
 */
function VersionPicker({ map, pinned, setPinned }) {
  const path = pathOf(map);
  const chosenName = pinned[path] ?? map.file.name;
  if (map.iterations.length < 2) return <span className={styles.iterationOne}>{chosenName}</span>;
  return (
    <select
      className={common.input}
      data-testid={`map-folder-version-${path}`}
      aria-label={`Version of ${map.name} to import`}
      value={chosenName}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setPinned((prev) => ({ ...prev, [path]: e.target.value }))}
    >
      {map.iterations.map((f) => (
        <option key={f.name} value={f.name}>
          {f.name}
        </option>
      ))}
    </select>
  );
}

/** A whole group, imported outright. */
function GroupImport({ group, tree, busy, importThese }) {
  return (
    <button
      className={common.btnSmall}
      data-testid={`map-folder-import-group-${group.name}`}
      disabled={busy || group.characters.length === 0}
      title={`Import every map in ${group.name}`}
      onClick={() => importThese(tree.groups.find((g) => g.name === group.name)?.characters ?? [], group.name)}
    >
      ⤓ {group.characters.length}
    </button>
  );
}

/** The folder's trees, memoised - and they must be; see the note in MapFolderView. */
function useFolderTree(files) {
  const tree = useMemo(() => (files?.length ? treeFromFileList(files) : null), [files]);
  const shelfTree = useMemo(() => (tree ? asShelfTree(tree) : null), [tree]);
  const folderName = useMemo(() => files?.[0]?.webkitRelativePath?.split('/')[0] ?? null, [files]);
  return { tree, shelfTree, folderName };
}

/**
 * THE FOLDER LIVES ABOVE THIS COMPONENT, as it does for characters: the
 * panel unmounts every time it loses focus, and with the tree in local
 * state that meant choosing the folder again on every visit.
 *
 * The shelf tree is MEMOISED, and it must be: `CharacterShelf` reports
 * the draw order back through an effect keyed on the tree object; a fresh
 * one each render makes that effect fire every render, and the setState
 * it calls renders again - an infinite loop that presents as the test
 * runner simply hanging.
 */
export default function MapFolderView({ files = null, onFiles, onImport }) {
  const dirRef = useRef(null);
  const { tree, shelfTree, folderName } = useFolderTree(files);
  const picks = useShelfPicks();
  const [pinned, setPinned] = useState({});
  const { busy, note, setNote, importThese } = useFolderImport(onImport, pinned);
  const readFolder = (picked) => {
    if (!picked || picked.length === 0) return;
    onFiles?.(Array.from(picked));
    picks.setSelected(new Set());
    setPinned({});
    setNote(null);
  };
  const choose = () => dirRef.current?.click();
  const input = <FolderInput inputRef={dirRef} onPick={readFolder} />;
  if (!tree) return <ClosedFolder input={input} onChoose={choose} />;
  const all = allFileCharacters(tree);
  const chosen = fileCharactersAt(tree, picks.selected);
  return (
    <div data-testid="map-folder-view">
      {input}
      <FolderActions
        {...{ all, chosen, busy, picks, importThese }}
        what={folderName || 'the folder'}
        onChange={choose}
      />
      {note && (
        <p className={styles.notice} data-testid="map-folder-note" role="status" aria-live="polite">
          {note}
        </p>
      )}
      <CharacterShelf
        tree={shelfTree}
        collapsed={picks.collapsed}
        onToggleGroup={picks.toggleGroup}
        picking
        selected={picks.selected}
        onToggleSelect={picks.toggleSelect}
        onSelectGroup={(paths) => picks.setSelected((prev) => toggleGroupSelection(prev, paths))}
        onOrder={(paths) => {
          picks.order.current = paths;
        }}
        groupActions={(group) => <GroupImport group={group} tree={tree} busy={busy} importThese={importThese} />}
        characterActions={(row) => <VersionPicker map={row.entry} pinned={pinned} setPinned={setPinned} />}
      />
      <p className={styles.hint} data-testid="map-folder-grammar">
        Click to pick · right-click to drop · shift+click for a range · ⛶ takes a whole folder
      </p>
    </div>
  );
}
